import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, Plus, Trash2, Check, X, Star } from 'lucide-react';
import { regionsService } from '../services/regions.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatMoney, currencySymbol } from '../utils/formatMoney';
import { useDialog } from '../contexts/DialogContext';

// Generic region catalog. A region bundles STATE common codes; region +
// service type drives the platform fee (platform_fee_rates). See
// platform_fee_regions.md.

const today = () => new Date().toISOString().slice(0, 10);

const customerFeeType = (r) =>
    r.platform_fee_fixed != null ? 'fixed' : r.commission_percent != null ? 'percent' : 'percent';
const customerFeeValue = (r) =>
    r.platform_fee_fixed != null
        ? String(r.platform_fee_fixed)
        : r.commission_percent != null
            ? String(r.commission_percent)
            : '';
const vendorFeeType = (r) =>
    r.vendor_platform_fee_fixed != null
        ? 'fixed'
        : r.vendor_commission_percent != null
            ? 'percent'
            : 'percent';
const vendorFeeValue = (r) =>
    r.vendor_platform_fee_fixed != null
        ? String(r.vendor_platform_fee_fixed)
        : r.vendor_commission_percent != null
            ? String(r.vendor_commission_percent)
            : '';
const formatFee = (type, value) => {
    if (value === '' || value == null) return '—';
    return type === 'fixed' ? formatMoney(value) : `${value}%`;
};

const emptyNewRate = () => ({
    service_type_code: '*',
    customerType: 'percent',
    customerValue: '',
    vendorType: 'percent',
    vendorValue: '',
    effective_from: today(),
});

const Regions = () => {
    const { confirm, prompt } = useDialog();
    const [regions, setRegions] = useState([]);
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [message, setMessage] = useState('');

    const [rates, setRates] = useState([]);
    const [ratesLoading, setRatesLoading] = useState(false);
    const [newRate, setNewRate] = useState(emptyNewRate);

    const selected = useMemo(
        () => regions.find((r) => r.id === selectedId) || null,
        [regions, selectedId],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setMessage('');
        try {
            const [rg, st] = await Promise.all([
                regionsService.listRegions(),
                regionsService.listStates(),
            ]);
            const items = rg.items || [];
            setRegions(items);
            setStates(st.items || []);
            setSelectedId((cur) => cur || (items[0] && items[0].id) || null);
        } catch (e) {
            setMessage(e?.message || 'Failed to load regions');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const loadRates = useCallback((regionId) => {
        if (!regionId) return;
        setRatesLoading(true);
        regionsService
            .listFeeRates(regionId)
            .then((res) => setRates(res.items || []))
            .catch((e) => setMessage(e?.message || 'Failed to load fee rules'))
            .finally(() => setRatesLoading(false));
    }, []);

    useEffect(() => { loadRates(selectedId); }, [selectedId, loadRates]);

    const createRegion = async () => {
        const name = await prompt({
            title: 'New region',
            label: 'Region name',
            required: true,
            confirmLabel: 'Create',
        });
        if (!name || !String(name).trim()) return;
        try {
            const r = await regionsService.createRegion({ name: String(name).trim() });
            await load();
            setSelectedId(r.id);
        } catch (e) {
            setMessage(e?.message || 'Could not create region');
        }
    };

    const patchRegion = async (patch) => {
        if (!selected) return;
        try {
            await regionsService.updateRegion(selected.id, patch);
            await load();
        } catch (e) {
            setMessage(e?.message || 'Could not update region');
        }
    };

    const removeRegion = async () => {
        if (!selected) return;
        const ok = await confirm({
            title: 'Delete region',
            message: `Delete region "${selected.name}"?`,
            confirmLabel: 'Delete',
        });
        if (!ok) return;
        try {
            await regionsService.deleteRegion(selected.id);
            setSelectedId(null);
            await load();
        } catch (e) {
            setMessage(e?.message || 'Could not delete region (still referenced by services?)');
        }
    };

    const toggleState = async (code) => {
        if (!selected) return;
        const owner = states.find((s) => s.code === code)?.region_id;
        if (owner && owner !== selected.id) return; // taken by another region
        const has = (selected.state_codes || []).includes(code);
        const next = has
            ? selected.state_codes.filter((c) => c !== code)
            : [...(selected.state_codes || []), code];
        await patchRegion({ state_codes: next });
    };

    const addRate = async () => {
        if (!selected) return;
        const cust = newRate.customerValue === '' ? null : parseFloat(newRate.customerValue);
        if (cust == null || Number.isNaN(cust) || cust < 0) {
            setMessage('Enter a valid customer platform fee');
            return;
        }
        const vendRaw = newRate.vendorValue === '' ? cust : parseFloat(newRate.vendorValue);
        if (vendRaw == null || Number.isNaN(vendRaw) || vendRaw < 0) {
            setMessage('Enter a valid vendor platform fee (or leave blank to match customer)');
            return;
        }
        try {
            await regionsService.createFeeRate({
                region_id: selected.id,
                service_type_code: (newRate.service_type_code || '*').trim() || '*',
                commission_percent: newRate.customerType === 'percent' ? cust : null,
                platform_fee_fixed: newRate.customerType === 'fixed' ? cust : null,
                vendor_commission_percent: newRate.vendorType === 'percent' ? vendRaw : null,
                vendor_platform_fee_fixed: newRate.vendorType === 'fixed' ? vendRaw : null,
                effective_from: newRate.effective_from || today(),
            });
            setNewRate(emptyNewRate());
            loadRates(selected.id);
        } catch (e) {
            setMessage(e?.message || 'Could not add fee rule');
        }
    };

    const saveRate = async (r, patch) => {
        try {
            await regionsService.updateFeeRate(r.id, patch);
            loadRates(selected.id);
        } catch (e) {
            setMessage(e?.message || 'Could not update fee rule');
        }
    };

    const deleteRate = async (r) => {
        const ok = await confirm({
            title: 'Delete fee rule',
            message: 'Delete this fee rule?',
            confirmLabel: 'Delete',
        });
        if (!ok) return;
        try {
            await regionsService.deleteFeeRate(r.id);
        } catch (_) { /* 204 has no body */ }
        loadRates(selected.id);
    };

    if (loading) return <div className="p-6"><LoadingSpinner /></div>;

    return (
        <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-6 h-6 text-gray-700" />
                <h1 className="text-xl font-semibold">Regions &amp; Platform Fee</h1>
            </div>
            <p className="text-sm text-gray-500 mb-4">
                A region groups STATE codes. A job&apos;s breakdown address picks the region;
                region + service type sets the <strong>customer</strong> platform fee (added to the quote)
                and the <strong>vendor</strong> platform fee (deducted from payout for Basic vendors;
                Pro / subscription vendors are waived). Self-service schemes only.
            </p>

            {message && (
                <div className="mb-3 text-sm text-red-600 flex items-center gap-2">
                    {message}
                    <button onClick={() => setMessage('')}><X className="w-3 h-3" /></button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Region list */}
                <div className="md:col-span-1">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Regions</span>
                        <button
                            onClick={createRegion}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded bg-blue-600 text-white">
                            <Plus className="w-3 h-3" /> New
                        </button>
                    </div>
                    <ul className="border rounded-lg divide-y">
                        {regions.map((r) => (
                            <li key={r.id}>
                                <button
                                    onClick={() => setSelectedId(r.id)}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                                        r.id === selectedId ? 'bg-blue-50 font-medium' : 'hover:bg-gray-50'
                                    }`}>
                                    <span className="flex items-center gap-1">
                                        {r.is_default && <Star className="w-3 h-3 text-amber-500" />}
                                        {r.name}
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                        {(r.state_codes || []).length} states
                                        {!r.is_active && ' · off'}
                                    </span>
                                </button>
                            </li>
                        ))}
                        {!regions.length && (
                            <li className="px-3 py-4 text-center text-gray-400 text-sm">No regions</li>
                        )}
                    </ul>
                </div>

                {/* Selected region */}
                <div className="md:col-span-2">
                    {!selected ? (
                        <div className="text-gray-400 text-sm">Select a region.</div>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                                <input
                                    defaultValue={selected.name}
                                    key={`name-${selected.id}`}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v && v !== selected.name) patchRegion({ name: v });
                                    }}
                                    className="border rounded px-3 py-1.5 text-sm font-medium"
                                />
                                <label className="text-sm flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={selected.is_default}
                                        onChange={(e) => patchRegion({ is_default: e.target.checked })}
                                    />
                                    Default
                                </label>
                                <label className="text-sm flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={selected.is_active}
                                        onChange={(e) => patchRegion({ is_active: e.target.checked })}
                                    />
                                    Active
                                </label>
                                <button
                                    onClick={removeRegion}
                                    className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 border rounded text-red-600">
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>

                            <div className="mb-6">
                                <div className="text-sm font-semibold text-gray-700 mb-2">STATE codes</div>
                                <div className="flex flex-wrap gap-2">
                                    {states.map((s) => {
                                        const mine = (selected.state_codes || []).includes(s.code);
                                        const takenElsewhere = s.region_id && s.region_id !== selected.id;
                                        return (
                                            <button
                                                key={s.code}
                                                onClick={() => toggleState(s.code)}
                                                disabled={takenElsewhere}
                                                title={takenElsewhere ? 'In another region' : ''}
                                                className={`px-2.5 py-1 rounded-full text-xs border ${
                                                    mine
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : takenElsewhere
                                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50'
                                                }`}>
                                                {s.value}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <div className="text-sm font-semibold text-gray-700 mb-2">Fee rules</div>
                                {ratesLoading ? (
                                    <LoadingSpinner />
                                ) : (
                                    <div className="overflow-x-auto border rounded-xl">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-500">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Service type</th>
                                                    <th className="text-left px-3 py-2">Customer fee</th>
                                                    <th className="text-left px-3 py-2">Vendor fee</th>
                                                    <th className="text-left px-3 py-2">From</th>
                                                    <th className="text-left px-3 py-2">To</th>
                                                    <th className="px-3 py-2">Active</th>
                                                    <th className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rates.map((r) => (
                                                    <tr key={r.id} className="border-t">
                                                        <td className="px-3 py-2 font-mono">{r.service_type_code}</td>
                                                        <td className="px-3 py-2">
                                                            {formatFee(customerFeeType(r), customerFeeValue(r))}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {formatFee(vendorFeeType(r), vendorFeeValue(r))}
                                                        </td>
                                                        <td className="px-3 py-2 text-gray-500">{r.effective_from}</td>
                                                        <td className="px-3 py-2 text-gray-500">{r.effective_to || '—'}</td>
                                                        <td className="px-3 py-2 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={r.is_active}
                                                                onChange={(e) => saveRate(r, { is_active: e.target.checked })}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <button
                                                                onClick={() => deleteRate(r)}
                                                                className="text-red-600 inline-flex items-center gap-1 text-xs">
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr className="border-t bg-gray-50/50">
                                                    <td className="px-3 py-2">
                                                        <input
                                                            value={newRate.service_type_code}
                                                            onChange={(e) => setNewRate((n) => ({ ...n, service_type_code: e.target.value }))}
                                                            className="w-24 border rounded px-2 py-1 text-sm font-mono"
                                                            placeholder="* or TOW"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex items-center gap-1">
                                                            <select
                                                                value={newRate.customerType}
                                                                onChange={(e) => setNewRate((n) => ({ ...n, customerType: e.target.value }))}
                                                                className="border rounded px-1 py-1 text-sm">
                                                                <option value="percent">%</option>
                                                                <option value="fixed">{currencySymbol()}</option>
                                                            </select>
                                                            <input
                                                                type="number" step="0.01" min="0"
                                                                value={newRate.customerValue}
                                                                onChange={(e) => setNewRate((n) => ({ ...n, customerValue: e.target.value }))}
                                                                className="w-20 border rounded px-2 py-1 text-sm"
                                                                placeholder={newRate.customerType === 'percent' ? '10' : '25.00'}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex items-center gap-1">
                                                            <select
                                                                value={newRate.vendorType}
                                                                onChange={(e) => setNewRate((n) => ({ ...n, vendorType: e.target.value }))}
                                                                className="border rounded px-1 py-1 text-sm">
                                                                <option value="percent">%</option>
                                                                <option value="fixed">{currencySymbol()}</option>
                                                            </select>
                                                            <input
                                                                type="number" step="0.01" min="0"
                                                                value={newRate.vendorValue}
                                                                onChange={(e) => setNewRate((n) => ({ ...n, vendorValue: e.target.value }))}
                                                                className="w-20 border rounded px-2 py-1 text-sm"
                                                                placeholder="same"
                                                                title="Blank = same as customer fee"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="date"
                                                            value={newRate.effective_from}
                                                            onChange={(e) => setNewRate((n) => ({ ...n, effective_from: e.target.value }))}
                                                            className="border rounded px-2 py-1 text-sm"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-300">—</td>
                                                    <td />
                                                    <td className="px-3 py-2 text-right">
                                                        <button
                                                            onClick={addRate}
                                                            className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded bg-blue-600 text-white">
                                                            <Check className="w-3 h-3" /> Add
                                                        </button>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <p className="text-xs text-gray-400 mt-2">
                                    Lookup picks the newest active rule with a matching service type,
                                    then falls back to <code>*</code>. Fixed fee wins over percent.
                                    Pro vendors skip the vendor fee; customers always pay the customer fee.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Regions;
